import type { AxiosError } from 'axios';
import { t } from 'i18next';
import { makeAutoObservable, ObservableMap, runInAction } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';
import * as qs from 'qs';
import { v4 as uuidv4 } from 'uuid';

import type { DeliveryMethods } from '~/api/ETA';
import type { ApiErrorResponse } from '~/api/Requests';
import * as Sentry from '~/services/sentry';
import { DOMAIN, REASON, reportFailure } from '~/services/telemetry';
import { cartStore, catalogStore, checkoutStore, orderStore } from '~/stores';
import { isReactNativeWebView } from '~/utils/isReactNativeWebView';

import { company } from '../company/Company';
import { CURRENCY_SYMBOL, OUTDATED_APP_POPUP_INTERVAL } from './constants';
import { storage as localStorage } from './LocalStorage';
import { storiesStore } from './StoriesStore';
import type { UserStore } from './UserStore';

type DeviceInfo = {
  platform: {
    ios?: {
      buildNumber: null;
      model: string | null;
      platform: string | null;
      systemVersion: string | null;
      userInterfaceIdiom: string | null;
    };
  };
  statusBarHeight?: number;
  bottomBarHeight?: number;
  isFirstLaunch: boolean;
  deviceInfo: {
    device_id: string | null;
    player_id: string | null;
  };
  nativeAppVersion: string;
  nativeBuildVersion: string;
  applePay: boolean;
  googlePay: boolean;
  codePushVersion: string;
};

type AlertTypes = 'error' | 'success';

interface AlertAction {
  label: string;
  onClick: () => void;
}

type Alert = {
  id: string;
  type: AlertTypes;
  message: string;
  onDismiss?: () => void;
  className?: string;
  action?: AlertAction;
  duration?: number;
};

type Analytics = {
  lastOrderId: string | null;
  lastOrderCreatedAt: string;
  lastOrderEta: string;
  lastOrderStatus: string;
  lastOrderStatusDate: string;
  firstOrderCreatedAt: string;
  promocodeUsedCount: number;
  totalSpent: string;
  totalOrders: number;
  totalSuccessfulOrders: number;
  hasTrackedFirstOpen: boolean;
};

export type Environment = 'production' | 'staging' | 'development' | 'stress';

export type GetRequestObj = Record<
  string,
  string | number | boolean | undefined
>;

export enum StartVerificationLocation {
  CART = 'cart',
  CABINET = 'cabinet',
  DEFAULT = '',
}

export class MainStore {
  private readonly _headerDropdownStateMap: ObservableMap = new ObservableMap<
    string,
    boolean
  >();

  isApplePay = false;
  isGooglePay = false;
  pageScrollY: Record<string, number> = {};
  alerts: Alert[] = [];
  isShowETAPopover = false;
  isShowServiceFeePopover = false;
  isShowNeedChangeAddressPopover = false;
  isShowMainMenu = false;
  isVisibleMainMenu = true;
  isShowGiftOutStockPopover = false;
  isShowGiftAlreadyReceivedPopover = false;
  isZeroCartPopover = false;
  isInvalidPromocodePopover = false;
  newDeliveryMethodForPromocodePopover: Nullable<DeliveryMethods | 'none'> =
    null;
  isAgeRestrictionPopover = false;
  isNotFindProductPopover = false;
  isOutdatedAppPopover = false;
  isRateOrderPopover = false;
  isPayTipPopover = false;
  isFreeDeliveryPopover = false;
  isChangeAddressPopover = false;
  outdatedAppPopoverExpired: number | null = null;
  isGeneralErrorPopover = false;
  isNoInternetPopover = false;
  isFuckupPopover = false;
  isSoonClosingPopover = false;
  lastDateShowSoonClosePopover: string | null = null;
  isShopClosedPopover = false;
  lastDateShowShopClosedPopover: string | null = null;
  rnBridgeCallbacks: Record<
    string,
    { onSuccess?: (data: any) => void; onError?: (data: any) => void }
  > = {};
  isRN = isReactNativeWebView();
  isAppInstalled = false;
  isRNReady = !import.meta.env.SSR && !window.ReactNativeWebView;
  startVerificationLocation: StartVerificationLocation =
    StartVerificationLocation.DEFAULT;
  inBackground = false;
  inAppBrowserClosedAt = 0;
  isBlockedPush = false;
  isShowSubscribePopover = false;
  appVersion = '';
  appBuildVersion = '';
  appPlatform: 'ios' | 'android' | '' = '';
  appCodePushVersion = '';
  environment: Environment = import.meta.env.REACT_APP_ENV || 'development';
  captchaSiteID =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem('captcha-dev-site-key')) ||
    company.envVariables.REACT_APP_TURNSTILE_SITE_KEY ||
    '';
  analytics: Analytics = {
    lastOrderId: null,
    lastOrderCreatedAt: '',
    lastOrderEta: '',
    lastOrderStatus: '',
    lastOrderStatusDate: '',
    firstOrderCreatedAt: '',
    promocodeUsedCount: 0,
    totalSpent: '0',
    totalOrders: 0,
    totalSuccessfulOrders: 0,
    hasTrackedFirstOpen: false,
  };
  deferedDeeplink = '';
  deferedRateOrder = false;
  isSafeRequest = false;
  loyaltyProgramSeen = false;
  onboardingSeen = false;
  digitsAfterComma = 2;
  isExpressDeliveryBannerClosed = false;

  constructor(protected readonly userStore: UserStore) {
    makeAutoObservable(this, {
      pageScrollY: false,
      rnBridgeCallbacks: false,
    });

    makePersistable(this, {
      name: 'MainStore',
      properties: [
        'analytics',
        'outdatedAppPopoverExpired',
        'isBlockedPush',
        'lastDateShowSoonClosePopover',
        'lastDateShowShopClosedPopover',
        'loyaltyProgramSeen',
        'onboardingSeen',
        'startVerificationLocation',
        'isAppInstalled',
      ],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  // Getters
  get isAllStoresSynchronized(): boolean {
    return (
      isHydrated(this) &&
      this.userStore.isSynchronized &&
      catalogStore.isSynchronized &&
      cartStore.isSynchronized &&
      orderStore.isSynchronized &&
      checkoutStore.isSynchronized &&
      storiesStore.isSynchronized
    );
  }

  get isOpenNewDeliveryMethodForPromocode() {
    return (
      this.newDeliveryMethodForPromocodePopover !== null &&
      this.newDeliveryMethodForPromocodePopover !== 'none'
    );
  }

  get isDeliveryBannerShown() {
    return !this.isExpressDeliveryBannerClosed;
  }

  // Setters
  pushAlert(
    type: AlertTypes,
    message: string,
    onDismiss?: () => void,
    className?: string,
    action?: AlertAction,
    duration?: number,
  ): void {
    const lastAlert = this.alerts[this.alerts.length - 1];
    if (this.alerts.length && lastAlert?.message === message) {
      return;
    }

    this.alerts.push({
      id: uuidv4(),
      type,
      message,
      onDismiss,
      className,
      action,
      duration,
    });
  }

  popAlert(): Alert | null {
    return this.alerts.pop() || null;
  }

  /**
   * Removes every alert that is not in `keep`.
   *
   * For flows that must not report to the customer at all: they snapshot the
   * stack before they start and hand it back here. Identity rather than a
   * count, because alerts dismiss themselves on a five-second timer, so a
   * length taken earlier describes a stack that no longer exists.
   */
  dismissAlertsExcept(keep: ReadonlySet<Alert>): void {
    const kept = this.alerts.filter((alert) => keep.has(alert));

    if (kept.length !== this.alerts.length) {
      this.alerts = kept;
    }
  }

  setStartVerificationLocation(location: StartVerificationLocation) {
    this.startVerificationLocation = location;
  }

  setIsShowETAPopover(flag: boolean) {
    this.isShowETAPopover = flag;
  }

  setIsAppInstalled(flag: boolean) {
    this.isAppInstalled = flag;
  }

  setIsShowServiceFeePopover(flag: boolean) {
    this.isShowServiceFeePopover = flag;
  }

  setIsNeedChangeAddressPopover(flag: boolean) {
    this.isShowNeedChangeAddressPopover = flag;
  }

  setIsShowGiftOutStockPopover(flag: boolean) {
    this.isShowGiftOutStockPopover = flag;
  }

  setIsShowGiftAlreadyReceivedPopover(flag: boolean) {
    this.isShowGiftAlreadyReceivedPopover = flag;
  }

  setIsZeroCartPopover(flag: boolean) {
    this.isZeroCartPopover = flag;
  }

  setIsInvalidPromocodePopover(flag: boolean) {
    this.isInvalidPromocodePopover = flag;
  }

  setNewDeliveryMethodForPromocodePopover(
    flag: Nullable<DeliveryMethods | 'none'>,
  ) {
    this.newDeliveryMethodForPromocodePopover = flag;
  }

  setIsAgeRestrictionPopover(flag: boolean) {
    this.isAgeRestrictionPopover = flag;
  }

  setIsNotFindProductPopover(flag: boolean) {
    this.isNotFindProductPopover = flag;
  }

  setIsRateOrderPopover(flag: boolean) {
    this.isRateOrderPopover = flag;
  }

  setIsPayTipPopover(flag: boolean) {
    this.isPayTipPopover = flag;
  }

  setIsFreeDeliveryPopover(flag: boolean) {
    this.isFreeDeliveryPopover = flag;
  }

  setIsChangeAddressPopover(flag: boolean) {
    this.isChangeAddressPopover = flag;
  }

  setIsOutdatedAppPopover(flag: boolean) {
    if (this.isRN && flag && !this.appVersion) {
      return;
    }
    if (
      flag &&
      this.outdatedAppPopoverExpired &&
      this.outdatedAppPopoverExpired > Date.now()
    ) {
      return;
    }
    this.outdatedAppPopoverExpired = Date.now() + OUTDATED_APP_POPUP_INTERVAL;
    this.isOutdatedAppPopover = flag;
  }

  setIsGeneralErrorPopover(flag: boolean) {
    this.isGeneralErrorPopover = flag;
  }

  setIsNoInternetPopover(flag: boolean) {
    this.isNoInternetPopover = flag;
  }

  setIsFuckupPopover(flag: boolean) {
    this.isFuckupPopover = flag;
  }

  setIsSoonClosingPopover(flag: boolean) {
    this.isSoonClosingPopover = flag;
  }

  setIsShopClosedPopover(flag: boolean) {
    this.isShopClosedPopover = flag;
  }

  setIsExpressDeliveryBannerClosed(flag: boolean) {
    runInAction(() => {
      this.isExpressDeliveryBannerClosed = flag;
    });
  }

  setLastDateShowSoonClosePopover(date: string) {
    this.lastDateShowSoonClosePopover = date;
  }

  setLastDateShowShopClosedPopover(date: string) {
    this.lastDateShowShopClosedPopover = date;
  }

  setPageScrollY(pageName: string, scrollY: number) {
    this.pageScrollY[pageName] = scrollY;
  }

  setIsRNReady(flag: boolean) {
    this.isRNReady = flag;
  }

  setInBackground(flag: boolean) {
    this.inBackground = flag;
  }

  setInAppBrowserClosedAt(timestamp: number) {
    this.inAppBrowserClosedAt = timestamp;
  }

  setIsBlockedPush(flag: boolean) {
    this.isBlockedPush = flag;
  }

  setIsShowSubscribePopover(flag: boolean) {
    /*if (![CompanyName.Jiffy].includes(company.name)) {
      this.isShowSubscribePopover = false;
      return;
    }*/
    this.isShowSubscribePopover = flag;
  }

  setIsShowMainMenu(flag: boolean) {
    this.isShowMainMenu = flag;
  }

  setIsVisibleMainMenu(flag: boolean) {
    this.isVisibleMainMenu = flag;
  }

  setDeferedDeeplink(link: string) {
    this.deferedDeeplink = link;
  }

  setDeferedRateOrder(flag: boolean) {
    this.deferedRateOrder = flag;
  }

  setLoyaltyProgramSeen(flag: boolean) {
    this.loyaltyProgramSeen = flag;
  }

  setOnboardingSeen(flag: boolean) {
    this.onboardingSeen = flag;
  }

  setDigitsAfterComma(val: number) {
    this.digitsAfterComma = val;
  }

  resetAnalytics() {
    const preservedFirstOpen = this.analytics.hasTrackedFirstOpen;
    this.analytics = {
      lastOrderId: null,
      lastOrderCreatedAt: '',
      lastOrderEta: '',
      lastOrderStatus: '',
      lastOrderStatusDate: '',
      firstOrderCreatedAt: '',
      promocodeUsedCount: 0,
      totalSpent: '0',
      totalOrders: 0,
      totalSuccessfulOrders: 0,
      hasTrackedFirstOpen: preservedFirstOpen,
    };
  }

  markFirstOpenTracked(): void {
    this.analytics.hasTrackedFirstOpen = true;
  }

  // Actions
  initRN(deviceInfo: DeviceInfo) {
    if (deviceInfo.bottomBarHeight !== undefined) {
      document.documentElement.style.setProperty(
        '--offset-bottom',
        deviceInfo.bottomBarHeight + 'px',
      );
    }
    if (deviceInfo.platform.ios) {
      this.appPlatform = 'ios';
    } else {
      this.appPlatform = 'android';
      this.isSafeRequest = true;
    }
    if (!this.userStore.deliveryAddress) {
      this.userStore.setIsFirstLaunch(true);
    } else if (deviceInfo.isFirstLaunch) {
      this.userStore.setIsFirstLaunch(true);
    }
    this.userStore.setDeviceId(deviceInfo.deviceInfo.device_id || '');
    this.userStore.setPlayerId(deviceInfo.deviceInfo.player_id || '');
    this.appVersion = deviceInfo.nativeAppVersion;
    this.appBuildVersion = deviceInfo.nativeBuildVersion;
    this.isApplePay = deviceInfo.applePay ?? false;
    this.isGooglePay = deviceInfo.googlePay ?? false;
    this.appCodePushVersion = deviceInfo.codePushVersion;
    this.checkFuckup(deviceInfo.deviceInfo.device_id || '');
  }

  checkFuckup(deviceId: string) {
    if (!deviceId || deviceId === '00000000-0000-0000-0000-000000000000') {
      this.setIsFuckupPopover(true);
      return;
    }
  }

  convertObjToGet(obj: GetRequestObj, trimEmpty = true): string {
    const keys: string[] = Object.keys(obj);
    let output = '';
    if (!keys.length) {
      return output;
    }
    for (let i = 0; i < keys.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (obj[keys[i]] === undefined) {
        continue;
      }
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (trimEmpty && obj[keys[i]] === '') {
        continue;
      }
      output += `&${keys[i]}=${encodeURIComponent(
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        obj[keys[i]] as string | number | boolean,
      )}`;
    }
    if (output) {
      output = '?' + output.slice(1);
    }

    /**
     * FYI: why not used qs (Mark E.)?
     *
     * trimEmpty not used
     */
    return `?${qs.stringify(obj)}`;
  }

  formatRange(
    start: string,
    props?: { end?: string; symbol?: string },
  ): string {
    const { end, symbol = '' } = props ?? {};
    const delimiter = '–';

    if (!start) {
      return '';
    }

    if (!end) {
      return `${symbol}${start.replace(
        /\s?[-—]\s?/gi,
        `${delimiter}${symbol}`,
      )}`;
    }

    return `${symbol}${start}${delimiter}${symbol}${end}`;
  }

  addZeroFraction(number: number | string, isTrimTrailingZeros = true): string {
    number = number.toString().replace(',', '.');
    const splitNumber = number.split('.');
    if (!splitNumber[1]) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      return isTrimTrailingZeros
        ? splitNumber[0]
        : `${splitNumber[0]}.${''.padEnd(this.digitsAfterComma, '0')}`;
    }
    if (isTrimTrailingZeros) {
      return parseFloat(number).toString();
    } else {
      return `${splitNumber[0]}.${splitNumber[1].padEnd(
        this.digitsAfterComma,
        '0',
      )}`;
    }
  }

  addLeadingZero(number: number): string {
    if (number > 9 || number < -9) {
      return number.toString();
    }
    if (number < 0) {
      return `-0${Math.abs(number)}`;
    }

    return `0${number}`;
  }

  toFloat(number: number | string): number {
    return parseFloat(number?.toString().replace(',', '.')) || 0;
  }

  isZero(number: number | string): boolean {
    return !this.toFloat(number);
  }

  additionCurrency(a: string | number, b: string | number): string {
    if (this.isZero(a) && this.isZero(b)) {
      return '0';
    }

    return this.convertPenceToPounds(
      this.convertPoundsToPence(a) + this.convertPoundsToPence(b),
    );
  }

  /**
   * TODO: THIS CODE SHOULD BE A PART OF MONEY CLASS
   * "subtractionCurrency" does not tell that it  converts money to pounds in result
   * Very dangerous side effect
   * */
  subtractionCurrency(a: string | number, b: string | number): string {
    if (this.isZero(a) && this.isZero(b)) {
      return '0';
    }

    return this.convertPenceToPounds(
      this.convertPoundsToPence(a) - this.convertPoundsToPence(b),
    );
  }

  convertPoundsToPence(number: number | string): NumberInt {
    if (number === undefined) {
      return number;
    }
    if (this.isZero(number)) {
      return 0;
    }
    return parseInt(this.addZeroFraction(number, false).replace('.', ''), 10);
  }

  convertPenceToPounds(number: NumberInt | string): string {
    if (number === undefined) {
      return number;
    }
    if (this.isZero(number)) {
      return this.addZeroFraction('0', company.isTrimTrailingZeros);
    }
    number = number.toString();
    const isNegative = number[0] === '-';
    if (isNegative) {
      number = number.slice(1);
    }
    number = number.padStart(this.digitsAfterComma + 1, '0');
    const letters = number.split('');
    letters.splice(this.digitsAfterComma * -1, 0, '.');
    return isNegative
      ? '-' +
          this.addZeroFraction(letters.join(''), company.isTrimTrailingZeros)
      : this.addZeroFraction(letters.join(''), company.isTrimTrailingZeros);
  }

  addPcs(num: number): string {
    return `${num}x`;
  }

  getPseudoId(): string {
    return (
      Date.now().toString().slice(-6) + Math.random().toString(32).slice(2, 12)
    );
  }

  prettyPhoneNumber(phone: string): string {
    if (!phone.length) {
      return '';
    }
    if (phone.startsWith('44')) {
      return phone
        .replace(/\D/g, '')
        .slice(-10)
        .replace(/(\d{4})(\d{3})(\d{3})/, '+44 ($1) $2-$3');
    }
    if (phone.startsWith('7')) {
      return phone
        .replace(/\D/g, '')
        .replace(/(\d{1,2})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5');
    }
    return '+' + phone;
  }

  calcDiscountPercentage(
    price: NumberInt,
    discountPrice: NumberInt,
  ): NumberInt {
    if (this.isZero(price) || this.isZero(discountPrice)) {
      return 0;
    }
    const discount = 100 - Math.ceil((discountPrice * 100) / price);
    return discount > 0 ? discount : 0;
  }

  flatArray(arr: any[], depth = 1): any[] {
    return depth > 0
      ? arr.reduce(
          (acc, val) =>
            acc.concat(
              Array.isArray(val) ? this.flatArray(val, depth - 1) : val,
            ),
          [],
        )
      : arr.slice();
  }

  sendToRN(
    targetFunc: string,
    data?: Record<string, any> | string | null,
    success?: (data: any) => void,
    error?: (data: any) => void,
  ) {
    if (!this.isRN) {
      return;
    }
    const msgObj: { targetFunc: string; data: any; msgId?: string } = {
      targetFunc,
      data: data || {},
    };
    if (success || error) {
      msgObj.msgId = this.getPseudoId();
      this.rnBridgeCallbacks[msgObj.msgId] = {
        onSuccess: success,
        onError: error,
      };
    }
    try {
      window.ReactNativeWebView?.postMessage(JSON.stringify(msgObj));
    } catch (e) {
      if (error) {
        error(msgObj);
        if (msgObj.msgId) {
          delete this.rnBridgeCallbacks[msgObj.msgId];
        }
      }
      console.error(e);
    }
  }

  setSentryUser({
    id,
    name,
    email,
    phone,
  }: {
    id?: string;
    name?: string;
    email?: string;
    phone?: string;
  }) {
    const userData: Record<string, string> = {
      ip_address: '{{auto}}',
    };
    if (id) {
      userData.id = id;
    }
    if (name) {
      userData.name = name;
    }
    if (email) {
      userData.email = email;
    }
    if (phone) {
      userData.phone = phone;
    } else if (this.userStore.personalData.phone) {
      userData.phone = this.userStore.personalData.phone;
    }
    Sentry.setUser(userData);
  }

  /**
   * The exact native artefact, as one string: version, build, and CodePush.
   *
   * Reported as the `app_build` tag through the telemetry context rather than
   * set by hand during the handshake. Doing it there meant reading the values
   * off the incoming payload before the store had them, and it left `Version`
   * as the only capitalised tag in the taxonomy — one more thing to remember
   * when searching.
   */
  get appBuild(): string {
    if (!this.appVersion) {
      return '';
    }
    const build = `${this.appVersion}-${this.appBuildVersion}`;
    return this.appCodePushVersion
      ? `${build}(${this.appCodePushVersion})`
      : build;
  }

  parseErrorMessage(
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): string {
    const { response } = error;
    if (!response) {
      if (error.code === 'ECONNABORTED') {
        return '';
      }
      if (error.message === 'Network Error') {
        this.setIsNoInternetPopover(true);
        return '';
      }
      return '';
    }
    if (response.data?.message && typeof response.data?.message === 'string') {
      if (context === 'requestAuthCode' && response.data?.status === 422) {
        return t('errors:INVALID_PHONE_NUMBER');
      }
      return response.data.message;
    }
    if (response.data?.errors?.length) {
      if (typeof response.data.errors[0] === 'string') {
        return response.data.errors[0];
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      } else if (response.data.errors[0].message) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        return response.data.errors[0].message;
      }
    }
    // The API answered with a shape this app cannot turn into a message, so
    // the user is shown nothing. Fingerprinted by the calling context: one
    // endpoint drifting is a different problem from all of them.
    reportFailure(DOMAIN.api, 'Unparseable API error', {
      reason: REASON.api.unparseableError,
      fingerprint: [context, String(response.status)],
      extras: {
        context,
        message: error.message || undefined,
        error: response,
      },
    });
    if (response.status >= 500) {
      this.setIsGeneralErrorPopover(true);
    }
    return '';
  }

  clearAllCache(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }

  validateEmail(email: string): boolean {
    email = email.trim();
    if (!email) {
      return false;
    }

    const emailParts = email.split('@');

    if (emailParts.length !== 2) {
      return false;
    }

    const [name = '', domain = ''] = emailParts;

    if (name.length > 64 || domain.length > 255) {
      return false;
    }
    if (domain.split('.').some((part) => part.length > 63)) {
      return false;
    }
    return new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(["'].+["']))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    ).test(email);
  }

  isSuitableAppVersion(reqVer: string): boolean {
    if (!reqVer) {
      return false;
    }
    if (!this.isRN) {
      return true;
    }
    if (!this.appVersion) {
      return false;
    }
    const reqVerSplit = reqVer.split('.');
    const appVerSplit = this.appVersion.split('.');
    if (reqVerSplit.length < 3 || appVerSplit.length < 3) {
      return false;
    }
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    if (parseInt(reqVerSplit[0], 10) < parseInt(appVerSplit[0], 10)) {
      return true;
    }
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    if (parseInt(reqVerSplit[0], 10) > parseInt(appVerSplit[0], 10)) {
      return false;
    }
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    if (parseInt(reqVerSplit[1], 10) < parseInt(appVerSplit[1], 10)) {
      return true;
    }
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    if (parseInt(reqVerSplit[1], 10) > parseInt(appVerSplit[1], 10)) {
      return false;
    }
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    return parseInt(reqVerSplit[2], 10) <= parseInt(appVerSplit[2], 10);
  }

  checkAndSetSubscribePopover(type: 'all' | 'onlyPush') {
    if (!this.isRN) {
      return;
    }
    let allEnabled;
    const personalisedPush =
      this.userStore.personalData.subscriptionFlags['personalised']?.push ||
      false;
    const discountsPush =
      this.userStore.personalData.subscriptionFlags['discounts']?.push || false;
    const promotionsPush =
      this.userStore.personalData.subscriptionFlags['third_party']?.push ||
      false;
    const allPushEnabled = personalisedPush && discountsPush && promotionsPush;
    if (type === 'all') {
      const personalisedEmail =
        this.userStore.personalData.subscriptionFlags['personalised']?.email ||
        false;
      const discountsEmail =
        this.userStore.personalData.subscriptionFlags['discounts']?.email ||
        false;
      const promotionsEmail =
        this.userStore.personalData.subscriptionFlags['third_party']?.email ||
        false;
      const allEmailEnabled = this.userStore.personalData.email
        ? personalisedEmail && discountsEmail && promotionsEmail
        : true;
      allEnabled = allPushEnabled && allEmailEnabled;
    } else {
      allEnabled = allPushEnabled;
    }
    if (!allEnabled) {
      this.setIsShowSubscribePopover(true);
    }
  }

  addCurrencySymbol(val: string | number, currency?: string): string {
    if (this.isZero(val)) {
      val = this.addZeroFraction(val, company.isTrimTrailingZeros);
    }
    const currencySymbol = currency
      ? CURRENCY_SYMBOL[currency] || ''
      : orderStore.currencySymbol;
    if ((currency || orderStore.currency) === 'aed') {
      return val + currencySymbol;
    } else {
      return currencySymbol + val;
    }
  }

  sortNumbers(a: number, b: number, sorting: 'asc' | 'desc' = 'asc'): number {
    const result = a < b ? -1 : a > b ? 1 : 0;
    if (!result || sorting === 'asc') {
      return result;
    }
    return result * -1;
  }

  sortAlphabet(a: string, b: string): number {
    if (!a || !b) {
      return 0;
    }
    return a.localeCompare(b);
  }

  isColorLight(color: string): boolean {
    if (!color) {
      return false;
    }
    if (color.toLowerCase() === 'white') {
      return true;
    }
    const split = /^#?([a-f\d]{1,2})([a-f\d]{1,2})([a-f\d]{1,2})$/i.exec(color);
    if (split?.length !== 4) {
      return false;
    }
    const [, r, g, b] = split.map((x) =>
      parseInt(x.length === 1 ? x + x : x, 16),
    );
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    return 0.299 * r + 0.587 * g + 0.114 * b > 230;
  }

  isOverlayExists(): boolean {
    return !!Array.from(this._headerDropdownStateMap?.values()).filter(Boolean)
      .length;
  }

  isHeaderDropdownOpen(observerKey: string): boolean {
    return this._headerDropdownStateMap.get(observerKey) ?? false;
  }

  setHeaderDropdownOpen(observerKey: string, value: boolean) {
    this._headerDropdownStateMap.set(observerKey, value);
  }

  consoleLog(message: any, type: 'log' | 'warn' | 'error' = 'log'): void {
    if (this.environment === 'production') {
      return;
    }
    if (type !== 'log') {
      // biome-ignore lint/suspicious/noConsole: console.log is used to log the message
      return console[type]('❗️', message);
    }
    // biome-ignore lint/suspicious/noConsole: console.log is used to log the message
    console[type](message);
  }

  // Errors
  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<never> => {
    if (error.response?.status === 401) {
      return Promise.reject(error);
    }
    if (context === 'requestPersonalData' && error.response?.status === 404) {
      //this.pushAlert('error', i18n.t('errors:userNotFound'));
      // The second way a session ends without the customer asking, and the one
      // nobody looks for: the token was valid, the customer lookup simply came
      // back empty. Silent before this, and indistinguishable afterwards from a
      // refresh that was refused.
      reportFailure(DOMAIN.auth, 'Profile not found', {
        reason: REASON.auth.profileNotFound,
        extras: { customerId: this.userStore.personalData.id },
      });
      this.userStore.logout({ cause: 'sessionExpired' }).catch(() => undefined);
      return Promise.reject(error);
    }
    const errorMessage = this.parseErrorMessage(error, context);
    if (errorMessage) {
      this.pushAlert('error', errorMessage);
    }
    return Promise.reject(error);
  };

  destroy() {
    this._headerDropdownStateMap.clear();
  }
}
